/**
 * useVisionARVisionPageLogic
 *
 * Pure logic hook — state machine, orbital bridge, actions.
 * Use the companion component for rendering.
 *
 * @generated from schema
 * @trait VisionARVisionPage
 */

import { useReducer, useCallback, useEffect, useRef, useMemo } from 'react';
import { useOrbitalBridge } from '@/hooks/useOrbitalBridge';
import type { EntityRow, EventPayload, EventPayloadValue, FieldValue } from '@almadar/core';
function getNestedValue(obj: EventPayload | EventPayloadValue | FieldValue | Record<string, FieldValue | undefined> | null | undefined, path: string): FieldValue | undefined {
  if (obj === null || obj === undefined || !path) return undefined;
  if (typeof obj !== 'object') return undefined;
  if (!path.includes('.') && !Array.isArray(obj)) {
    return (obj as Record<string, FieldValue | undefined>)[path];
  }
  let value: EventPayload | EventPayloadValue | FieldValue | Record<string, FieldValue | undefined> | null | undefined = obj;
  for (const part of path.split('.')) {
    if (value === null || value === undefined || typeof value !== 'object') return undefined;
    if (Array.isArray(value)) {
      if (!/^\d+$/.test(part)) return undefined;
      value = (value as readonly (EventPayloadValue | FieldValue | null | undefined)[])[Number(part)];
      continue;
    }
    value = (value as Record<string, FieldValue | undefined>)[part];
  }
  return value as FieldValue | undefined;
}
import type { EventResponse, VisionARVisionView as VisionARVisionViewEntity, VisionARVisionPageEventPayloadMap } from '@app/shared';
import { useUIEvents } from '@almadar/ui/hooks';
import { registerTraitSnapshot, type TraitStateSnapshot } from '@almadar/ui/lib';
import { createCommandSendPump } from '@almadar/ui/lib';

// ============================================================================
// State Machine
// ============================================================================

export type VisionARVisionPageState =
  | 'idle';

export type VisionARVisionPageEvent =
  | 'INIT';

type VisionARVisionPageSlot = 'main';
interface VisionARVisionPageInternalState {
  machineState: VisionARVisionPageState;
  data: Record<string, EntityRow[]>;
  lastPayload: EventPayload | undefined;
  lastEvent: VisionARVisionPageEvent | null;
  slotWriter: Partial<Record<VisionARVisionPageSlot, VisionARVisionPageEvent | null>>;
  loading: boolean;
  pendingEvent: VisionARVisionPageEvent | null;
  pendingFrom: string | null;
  error: string | null;
  fields: Partial<VisionARVisionViewEntity>;
}

type VisionARVisionPageAction =
  | { type: 'EVENT_SUCCESS'; event: VisionARVisionPageEvent; newState: string; data: Record<string, EntityRow[]>; payload?: EventPayload; fields?: Record<string, EventPayloadValue | undefined>; renderBearing?: boolean; renderWrite?: VisionARVisionPageSlot[]; renderClear?: VisionARVisionPageSlot[] }
  | { type: 'EVENT_ERROR'; error: string }
  | { type: 'RESET_ERROR' }
  | { type: 'START_EVENT'; event: VisionARVisionPageEvent; from: string }
  | { type: 'END_EVENT' }
  | { type: 'SET_FIELD'; field: keyof VisionARVisionViewEntity & string; value: EventPayloadValue | undefined }
  | { type: 'TICK'; updates: Record<string, EventPayloadValue | undefined>; collection: string };

function visionARVisionPageReducer(
  state: VisionARVisionPageInternalState,
  action: VisionARVisionPageAction
): VisionARVisionPageInternalState {
  switch (action.type) {
    case 'START_EVENT':
      return { ...state, loading: true, pendingEvent: action.event, pendingFrom: action.from };
    case 'END_EVENT':
      return { ...state, loading: false, pendingEvent: null, pendingFrom: null };
    case 'EVENT_SUCCESS':
      return {
        machineState: action.newState as VisionARVisionPageState,
        data: { ...state.data, ...action.data },
        lastPayload: action.renderBearing ? (action.payload ?? state.lastPayload) : state.lastPayload,
        lastEvent: action.renderBearing ? action.event : state.lastEvent,
        slotWriter: {
          ...state.slotWriter,
          ...Object.fromEntries((action.renderWrite ?? []).map((s) => [s, action.event] as const)),
          ...Object.fromEntries((action.renderClear ?? []).map((s) => [s, null] as const)),
        },
        loading: false,
        pendingEvent: null,
        pendingFrom: null,
        error: null,
        fields: { ...state.fields, ...(action.fields ?? {}) } as Partial<VisionARVisionViewEntity>,
      };
    case 'EVENT_ERROR':
      return { ...state, loading: false, pendingEvent: null, pendingFrom: null, error: action.error };
    case 'RESET_ERROR':
      return { ...state, error: null };
    case 'SET_FIELD': {
      // Back-compat for non-codegen consumers that still dispatch
      // SET_FIELD directly. Codegen folds field updates into
      // EVENT_SUCCESS so transitions commit in a single render.
      return {
        ...state,
        fields: { ...state.fields, [action.field]: action.value } as Partial<VisionARVisionViewEntity>,
      };
    }
    case 'TICK': {
      // Batch-update entity fields for tick effects. A [runtime]-linked
      // trait's row lives in the `fields` mirror (transitions write it,
      // the view reads it), so updates ALWAYS merge there; the
      // `data` collection row is updated too when one exists.
      const tickFields = { ...state.fields, ...action.updates } as Partial<VisionARVisionViewEntity>;
      const arr = state.data[action.collection];
      const target = arr?.[0];
      if (!target) return { ...state, fields: tickFields };
      const updated = { ...target, ...action.updates } as EntityRow;
      return {
        ...state,
        data: { ...state.data, [action.collection]: [updated, ...arr.slice(1)] },
        fields: tickFields,
      };
    }
    default:
      return state;
  }
}

const initialState: VisionARVisionPageInternalState = {
  machineState: 'idle',
  data: {},
  lastPayload: undefined,
  lastEvent: null,
  slotWriter: {},
  loading: false,
  pendingEvent: null,
  pendingFrom: null,
  error: null,
  fields: {},
};

// ============================================================================
// Hook
// ============================================================================

export interface VisionARVisionPageConfig {
  linkedEntity?: string;
  entityId?: string;
  routeParams?: Record<string, string | undefined>;
  initPayload?: EventPayload;
  convergeArt: string;
  keepArt: string;
  proposeArt: string;
  sourceArt: string;
}

const DEFAULT_VISION_AR_VISION_PAGE_CONFIG = {
  convergeArt: "<svg class='vc' viewBox='0 0 480 360' xmlns='http://www.w3.org/2000/svg' role='img' aria-hidden='true'><style>.vc .frame{fill:none;stroke:var(--color-primary);stroke-width:2;stroke-dasharray:640;stroke-dashoffset:640;animation:vc-frame 7s ease-in-out infinite} .vc .t{fill:var(--color-card);stroke:var(--color-border);stroke-width:1.5;animation:vc-in 7s cubic-bezier(.16,1,.3,1) infinite} .vc .bar{fill:var(--color-primary);opacity:0;animation:vc-bar 7s ease-in-out infinite} @keyframes vc-in{0%,12%{transform:translate(var(--dx),var(--dy)) rotate(var(--r))}40%,82%{transform:translate(0,0) rotate(0)}100%{transform:translate(var(--dx),var(--dy)) rotate(var(--r))}} @keyframes vc-frame{0%,38%{stroke-dashoffset:640}55%,82%{stroke-dashoffset:0}100%{stroke-dashoffset:640}} @keyframes vc-bar{0%,45%{opacity:0}58%,82%{opacity:.9}100%{opacity:0}} @media (prefers-reduced-motion:reduce){.vc *{animation:none!important}}</style><rect class='frame' x='118' y='92' width='244' height='176' rx='14'/><rect class='bar' x='132' y='104' width='216' height='12' rx='6'/><rect class='t' x='132' y='126' width='70' height='46' rx='8' style='--dx:-110px;--dy:-80px;--r:-14deg;animation-delay:0s'/><rect class='t' x='205' y='126' width='70' height='46' rx='8' style='--dx:10px;--dy:-105px;--r:9deg;animation-delay:0.08s'/><rect class='t' x='278' y='126' width='70' height='46' rx='8' style='--dx:120px;--dy:-70px;--r:16deg;animation-delay:0.16s'/><rect class='t' x='132' y='192' width='70' height='46' rx='8' style='--dx:-120px;--dy:70px;--r:12deg;animation-delay:0.24s'/><rect class='t' x='205' y='192' width='70' height='46' rx='8' style='--dx:-5px;--dy:110px;--r:-10deg;animation-delay:0.32s'/><rect class='t' x='278' y='192' width='70' height='46' rx='8' style='--dx:115px;--dy:85px;--r:-16deg;animation-delay:0.4s'/></svg>",
  keepArt: "<svg class='vk' viewBox='0 0 480 360' xmlns='http://www.w3.org/2000/svg' role='img' aria-hidden='true'><style>.vk .db{fill:var(--color-card);stroke:var(--color-border);stroke-width:1.5} .vk .db2{fill:var(--color-muted)} .vk .folder{fill:var(--color-primary);opacity:.9} .vk .tab{fill:var(--color-primary)} .vk .path{fill:none;stroke:var(--color-border);stroke-width:2;stroke-dasharray:4 8} .vk .rec{fill:var(--color-primary);animation:vk-move 4s cubic-bezier(.45,0,.2,1) infinite} .vk .r2{animation-delay:1.33s} .vk .r3{animation-delay:2.66s} .vk .key{fill:none;stroke:var(--color-foreground);stroke-width:4;stroke-linecap:round;transform-origin:118px 112px;animation:vk-key 4s ease-in-out infinite} @keyframes vk-move{0%{transform:translate(0,0);opacity:0}10%{opacity:1}85%{opacity:1}100%{transform:translate(222px,0);opacity:0}} @keyframes vk-key{0%,60%,100%{transform:rotate(0)}70%,80%{transform:rotate(-30deg)}} @media (prefers-reduced-motion:reduce){.vk *{animation:none!important}}</style><ellipse class='db2' cx='130' cy='150' rx='62' ry='18'/><path class='db' d='M68 150 v80 c0 10 28 18 62 18 s62-8 62-18 v-80'/><ellipse class='db' cx='130' cy='150' rx='62' ry='18'/><path class='db' d='M68 190 c0 10 28 18 62 18 s62-8 62-18' fill='none'/><circle class='key' cx='118' cy='96' r='14'/><path class='key' d='M118 110 v36 m0-14 h10 m-10 10 h8'/><path class='path' d='M196 205 H352'/><rect class='rec' x='176' y='196' width='22' height='18' rx='4'/><rect class='rec r2' x='176' y='196' width='22' height='18' rx='4'/><rect class='rec r3' x='176' y='196' width='22' height='18' rx='4'/><path class='tab' d='M360 150 h40 l12 14 h0 v6 h-52z'/><rect class='folder' x='360' y='164' width='92' height='80' rx='10'/></svg>",
  proposeArt: "<svg class='vp' viewBox='0 0 480 360' xmlns='http://www.w3.org/2000/svg' role='img' aria-hidden='true'><style>.vp .card{fill:var(--color-card);stroke:var(--color-border);stroke-width:1.5} .vp .row{fill:var(--color-muted)} .vp .sug{fill:var(--color-primary);opacity:0;transform:translateX(-24px);animation:vp-sug 6s cubic-bezier(.16,1,.3,1) infinite} .vp .spark{fill:var(--color-primary);opacity:0;animation:vp-spark 6s ease-in-out infinite} .vp .who{fill:var(--color-foreground)} .vp .ok{fill:none;stroke:var(--color-primary);stroke-width:4;stroke-linecap:round;stroke-linejoin:round;stroke-dasharray:60;stroke-dashoffset:60;animation:vp-ok 6s ease-in-out infinite} .vp .ring{fill:none;stroke:var(--color-primary);stroke-width:2;opacity:0;animation:vp-ring 6s ease-in-out infinite} @keyframes vp-sug{0%,12%{opacity:0;transform:translateX(-24px)}25%,88%{opacity:.25;transform:translateX(0)}100%{opacity:0}} @keyframes vp-spark{0%,10%{opacity:0}20%,40%{opacity:1}55%,100%{opacity:0}} @keyframes vp-ok{0%,50%{stroke-dashoffset:60}65%,88%{stroke-dashoffset:0}100%{stroke-dashoffset:60}} @keyframes vp-ring{0%,48%{opacity:0}58%,88%{opacity:1}100%{opacity:0}} @media (prefers-reduced-motion:reduce){.vp *{animation:none!important}}</style><rect class='card' x='70' y='60' width='280' height='240' rx='16'/><rect class='row' x='94' y='88' width='200' height='14' rx='7'/><rect class='row' x='94' y='120' width='232' height='14' rx='7'/><rect class='sug' x='90' y='148' width='240' height='34' rx='9'/><rect class='row' x='94' y='158' width='170' height='14' rx='7'/><path class='spark' d='M318 150 l5 11 11 5 -11 5 -5 11 -5 -11 -11 -5 11 -5z'/><rect class='row' x='94' y='196' width='210' height='14' rx='7'/><rect class='row' x='94' y='228' width='150' height='14' rx='7'/><circle class='who' cx='392' cy='236' r='18'/><path class='who' d='M362 300 c0-24 60-24 60 0z'/><circle class='ring' cx='392' cy='150' r='26'/><path class='ok' d='M380 150 l9 9 17-18'/></svg>",
  sourceArt: "<svg class='vs' viewBox='0 0 480 360' xmlns='http://www.w3.org/2000/svg' role='img' aria-hidden='true'><style>.vs .doc{fill:var(--color-primary)} .vs .ln{stroke:var(--color-primary-foreground);stroke-width:3;stroke-linecap:round} .vs .flow{fill:none;stroke:var(--color-primary);stroke-width:2;stroke-dasharray:6 10;animation:vs-flow 1.6s linear infinite} .vs .node{fill:var(--color-card);stroke:var(--color-border);stroke-width:1.5;transform-box:fill-box;transform-origin:center;animation:vs-pop 4.5s ease-in-out infinite} .vs .n2{animation-delay:1.5s} .vs .n3{animation-delay:3s} .vs .mk{fill:var(--color-muted-foreground)} @keyframes vs-flow{to{stroke-dashoffset:-32}} @keyframes vs-pop{0%,100%{transform:scale(1);stroke:var(--color-border)}15%{transform:scale(1.08);stroke:var(--color-primary)}30%{transform:scale(1)}} @media (prefers-reduced-motion:reduce){.vs *{animation:none!important}}</style><path class='flow' d='M240 150 C240 110 140 110 110 80'/><path class='flow' d='M240 150 C240 110 340 110 370 80'/><path class='flow' d='M240 210 L240 270'/><rect class='node' x='60' y='38' width='100' height='70' rx='10'/><rect class='mk' x='72' y='50' width='76' height='8' rx='4'/><rect class='mk' x='72' y='66' width='36' height='30' rx='4'/><rect class='mk' x='112' y='66' width='36' height='30' rx='4'/><rect class='node n2' x='320' y='38' width='100' height='70' rx='10'/><rect class='mk' x='334' y='52' width='72' height='6' rx='3'/><rect class='mk' x='334' y='66' width='60' height='6' rx='3'/><rect class='mk' x='334' y='80' width='66' height='6' rx='3'/><rect class='node n3' x='180' y='262' width='120' height='60' rx='30'/><circle class='mk' cx='212' cy='292' r='10'/><rect class='mk' x='230' y='286' width='52' height='12' rx='6'/><rect class='doc' x='196' y='140' width='88' height='80' rx='12'/><line class='ln' x1='212' y1='162' x2='268' y2='162'/><line class='ln' x1='212' y1='180' x2='256' y2='180'/><line class='ln' x1='212' y1='198' x2='262' y2='198'/></svg>",
} as VisionARVisionPageConfig;

export interface VisionARVisionPageLogicReturn {
  state: VisionARVisionPageState;
  data: Record<string, EntityRow[]>;
  lastPayload: EventPayload | undefined;
  lastEvent: VisionARVisionPageEvent | null;
  slotWriter: Partial<Record<VisionARVisionPageSlot, VisionARVisionPageEvent | null>>;
  loading: boolean;
  pendingEvent: VisionARVisionPageEvent | null;
  pendingFrom: string | null;
  error: string | null;
  fields: Partial<VisionARVisionViewEntity>;
  dispatch: (event: string, payload?: EventPayload) => Promise<EventResponse>;
  actions: { [K in keyof VisionARVisionPageEventPayloadMap]: (payload?: VisionARVisionPageEventPayloadMap[K]) => Promise<EventResponse> };
  config: VisionARVisionPageConfig;
}

export function useVisionARVisionPageLogic(config?: Partial<VisionARVisionPageConfig>): VisionARVisionPageLogicReturn {
  const mergedConfig: VisionARVisionPageConfig = { ...DEFAULT_VISION_AR_VISION_PAGE_CONFIG, ...(config ?? {}) };

  // Orbital bridge: event bus + server communication
  const { sendEvent, eventBus } = useOrbitalBridge({ basePath: '/api/vision-ar-vision-page', orbitalName: 'VisionAR', traitName: 'VisionARVisionPage' });

  // Internal state with reducer
  const [internalState, dispatchReducer] = useReducer(visionARVisionPageReducer, initialState);
  const { machineState: state, data, lastPayload, lastEvent, slotWriter, loading, pendingEvent, pendingFrom, error, fields: _initialFields } = internalState;
  const fields = _initialFields;
  const internalStateRef = useRef(internalState);
  internalStateRef.current = internalState;

  const machineStateRef = useRef(state);
  machineStateRef.current = state;

  const fieldsRef = useRef<EntityRow>({ ...fields });
  fieldsRef.current = { ...fields };
  const commandPump = useMemo(() => createCommandSendPump(), []);

  // Actor model: sequential event queue (prevents cross-trait race conditions)
  const eventQueueRef = useRef<Array<{ event: string; payload?: EventPayload; settle?: () => void }>>([]);
  const processingRef = useRef(false);

  // Transpiled client-side effects (Phase 4)
  // Each (fromState, event) transition's client effects are compiled to TypeScript.
  const executeTransitionEffects = useCallback((fromState: string, event: string, liveData: Record<string, EntityRow[]>, _payload?: EventPayload): { fields: Record<string, EventPayloadValue | undefined>; newState: string | undefined; emits: Array<() => void> } => {
    const _fields: Record<string, EventPayloadValue | undefined> = {};
    const _emits: Array<() => void> = [];
    let _newState: string | undefined = undefined;
    const entity: EntityRow[] = liveData['VisionARVisionView'] ?? [];
    let fields: Record<string, EventPayloadValue | undefined> = entity[0] ?? {};

    if (fromState === 'idle' && event === 'INIT') {
      // idle → idle (on INIT)
      _newState = 'idle';
    }
    return { fields: _fields, newState: _newState, emits: _emits };
  }, [eventBus]);

  // Per-transition render-ui presence map (codegen).
  // Used by dispatch to skip lastPayload/lastEvent updates for
  // transitions whose only effects are server-side or set/emit —
  // those don't change the rendered JSX, so the view should keep
  // the prior frame's payload until a render-bearing transition
  // (e.g. fetch.emit.success cascade) lands.
  const getRenderUiSlots = useCallback((fromState: string, event: string): { write: VisionARVisionPageSlot[]; clear: VisionARVisionPageSlot[] } => {
    switch (fromState) {
      case 'idle':
        switch (event) {
          case 'INIT': return { write: ['main'] as VisionARVisionPageSlot[], clear: [] as VisionARVisionPageSlot[] };
          default: return { write: [], clear: [] };
        }
      default:
        return { write: [], clear: [] };
    }
  }, []);

  /**
   * Dispatch an event through the orbital bridge.
   * Bridge emits lifecycle events on the bus, forwards to server.
   * Client effects are transpiled at compile time (Phase 4).
   */
  const dispatch = useCallback(async (event: string, __rawPayload?: EventPayload): Promise<EventResponse> => {
    const payload: EventPayload | undefined =
      __rawPayload !== undefined && ('entityId' in __rawPayload || 'id' in __rawPayload)
        ? __rawPayload
        : config?.entityId !== undefined
          ? { ...(__rawPayload ?? {}), entityId: config.entityId }
          : __rawPayload;
    const prevState = machineStateRef.current;
    // T9 optimistic dispatch: the fields mirror IS the [runtime] entity
    // row, so guard + to-state resolve locally on the same data the
    // server would read. Commit first, POST after; reconcile below.
    const liveData: Record<string, EntityRow[]> = { 'VisionARVisionView': [fieldsRef.current as EntityRow] };
    const _optimistic = executeTransitionEffects(prevState, event, liveData, payload);
    if (_optimistic.newState === undefined) {
      // Guard rejected or no transition defined — mirrors the server's
      // rejection, without the round trip.
      const errorMsg = `No transition defined for ${event} from ${prevState} (or guard rejected)`;
      dispatchReducer({ type: 'EVENT_ERROR', error: errorMsg });
      return { success: false, transitioned: false, states: {}, emittedEvents: [], error: errorMsg } as EventResponse;
    }
    const _renderSlots = getRenderUiSlots(prevState, event);
    const _renderBearing = _renderSlots.write.length > 0 || _renderSlots.clear.length > 0;
    // Snapshot the touched fields' prior values for a reconcile rollback.
    const _snapshot: Record<string, EventPayloadValue | undefined> = {};
    for (const _k of Object.keys(_optimistic.fields)) { _snapshot[_k] = fieldsRef.current[_k]; }
    dispatchReducer({
      type: 'EVENT_SUCCESS',
      event: event as VisionARVisionPageEvent,
      newState: _optimistic.newState,
      data: {},
      payload,
      fields: _optimistic.fields,
      renderBearing: _renderBearing,
      renderWrite: _renderSlots.write,
      renderClear: _renderSlots.clear,
    });
    machineStateRef.current = _optimistic.newState as typeof machineStateRef.current;
    fieldsRef.current = { ...fieldsRef.current, ..._optimistic.fields } as EntityRow;
    for (const _emit of _optimistic.emits) _emit();

    void sendEvent; void commandPump; // no server-touching effect on this trait; shared-store commit above is the only sync needed
    return { success: true, transitioned: true, states: { 'VisionARVisionPage': _optimistic.newState }, emittedEvents: [] } as EventResponse;
  }, [sendEvent, executeTransitionEffects, commandPump, config?.entityId]);

  // Actor model: drain event queue one at a time
  const drainQueue = useCallback(async () => {
    if (processingRef.current) return;
    processingRef.current = true;
    try {
      while (eventQueueRef.current.length > 0) {
        const entry = eventQueueRef.current.shift()!;
        try { await dispatch(entry.event, entry.payload); } finally { entry.settle?.(); }
      }
    } finally {
      processingRef.current = false;
    }
  }, [dispatch]);

  // Actor model: enqueue event and start drain
  const enqueueEvent = useCallback((event: string, payload?: EventPayload) => {
    return new Promise<void>((settle) => {
    eventQueueRef.current.push({ event, payload, settle });
    void drainQueue();
    });
  }, [drainQueue]);

  // Action creators (typed per-event)
  const actions: { [K in keyof VisionARVisionPageEventPayloadMap]: (payload?: VisionARVisionPageEventPayloadMap[K]) => Promise<EventResponse> } = {
    'INIT': () => dispatch('INIT'),
  };

  useUIEvents(enqueueEvent, 'VisionAR.VisionARVisionPage', ['INIT'] as const, eventBus);

  // Verifier snapshot registration (VG Foundation 1)
  useEffect(() => {
    const unregister = registerTraitSnapshot('VisionARVisionPage', (): TraitStateSnapshot => ({
      traitName: 'VisionARVisionPage',
      currentState: internalStateRef.current.machineState,
      states: ['idle'],
      events: ['INIT'],
      data: internalStateRef.current.data,
      lastPayload: internalStateRef.current.lastPayload,
      lastEventDispatched: internalStateRef.current.lastEvent !== null
        ? {
            event: internalStateRef.current.lastEvent,
            ...(internalStateRef.current.lastPayload !== undefined
              ? { payload: internalStateRef.current.lastPayload }
              : {}),
            timestamp: Date.now(),
          }
        : undefined,
      cascadeReceived: [],
    }));
    return unregister;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-init on mount
  useEffect(() => {
    const __rp = Object.fromEntries(Object.entries(config?.routeParams ?? {}).filter(([, v]) => v !== undefined));
    const __init = { ...__rp, ...(config?.initPayload ?? {}), ...(config?.entityId !== undefined ? { entityId: config.entityId } : {}) };
    dispatch('INIT', Object.keys(__init).length > 0 ? __init : undefined);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    state,
    data,
    lastPayload,
    lastEvent,
    slotWriter,
    loading,
    pendingEvent,
    pendingFrom,
    error,
    fields,
    dispatch,
    actions,
    config: mergedConfig,
  };
}
